import * as React from "react";
import { merge, $ } from "glamor";
import {
    INotification,
    INotificationFilters,
    IFeedEntry,
    INotificationStatusParams,
    IExportNotificationStatusParams,
    IExportNotitications,
} from "reducers/notifications";
import { formatMessage } from "lib/globalize-util";
import { theme } from "styles/theme";
import { UserActivity } from "./user-activity";
import { ActivityFeed } from "components/activity-feed";
import { stringify } from "querystring";
import me from "models/me";
import siteConfig from "site-config";
import Cancel from "components/icons/cancel";
import FlatButton from "material-ui/FlatButton";

import { COLLECTION_FEED_SLUG } from "constants/product-collection";
import { PAGE_SIZE } from "epics/timeline";
import { useDebounce } from "hooks/useDebounce";
import { DATATEST } from "constants/integration";
import { IExport } from "containers/folders/types/export";
import { safeParseInt } from "lib/number";

interface IProps {
    panelWidth: number;
    onFetchUserNotification: (filters?: INotificationFilters, next?: boolean) => void;
    userNotification: INotification[];
    userNotificationFetching: boolean;
    userNotificationHasNextPage: boolean;
    onLoadUserNotificationPolledEntries: (userNotification: INotification[]) => void;
    onRequestClose: () => void;
    collectionFeedStats: IFeedEntry;
    onUpdateUserNotifications: (params: INotificationStatusParams) => void;
    merchantId: string;
    exportNotifications?: IExportNotitications;
    onExportWhiteboards?: (exportItem: IExport) => void;
    onUpdateExportNotificationStatus?: (params: IExportNotificationStatusParams) => void;
}

export const UserNotification: React.FunctionComponent<IProps> = ({
    panelWidth,
    onFetchUserNotification,
    userNotification,
    userNotificationFetching,
    userNotificationHasNextPage,
    onLoadUserNotificationPolledEntries,
    onRequestClose,
    collectionFeedStats,
    onUpdateUserNotifications,
    merchantId,
    exportNotifications,
    onExportWhiteboards,
    onUpdateExportNotificationStatus,
}) => {
    const styles = React.useMemo(() => {
        return merge(
            {
                height: "100%",
                paddingTop: "0px",
                paddingBottom: "8px",
                flexShrink: 0,
                flexGrow: 0,
                width: `${panelWidth}px`,
                position: "relative",
            },
            $(" .userNotificationContainer", {
                boxShadow: "0 2px 2px 0 rgba(0,0,0,.14), 0 3px 1px -2px rgba(0,0,0,.2), 0 1px 5px 0 rgba(0,0,0,.12)",
                zIndex: 1000,
                position: "absolute",
                display: "flex",
                flexDirection: "column",
                top: "0px",
                left: 0,
                right: 0,
                bottom: 0,
                background: `${theme.palette.canvasColor}`,
            }),
            $(" .userNotificationContainer > div:last-child", {
                overflowY: "auto",
            }),
            $(" .userNotificationContainer:before", {
                content: " ",
                display: "block",
                width: 0,
                height: 0,
                position: "absolute",
                borderTop: "0px solid transparent",
                borderBottom: `12px solid ${theme.palette.lighterGrey}`,
                borderRight: "12px solid transparent",
                borderLeft: "12px solid transparent",
                left: "50%",
                transform: "translateX(-50%)",
                top: "-8px",
                zIndex: 2,
            }),
            $(" .timelineBody", {
                flexGrow: 1,
                flexShrink: 1,
                height: "100px",
            }),
            $(" .userNotificationHeaderTitle", {
                lineHeight: "57px",
                height: "57px",
                overflow: "hidden",
                backgroundColor: `${theme.palette.lighterGrey}`,
                fontSize: "14px",
                padding: "0 16px",
                margin: "0",
                borderBottom: `1px solid ${theme.palette.borderColor}`,
                borderTop: `1px solid ${theme.palette.borderColor}`,
                color: theme.palette.darkerGrey,
                flexGrow: 0,
                flexShrink: 0,
                fontWeight: 700,
                zIndex: 1,
            }),
            $(" .userNotificationHeader", {
                zIndex: 1,
            }),
            $(" .timelineCloseButton", {
                float: "right",
                minWidth: "25px !important",
                marginTop: "5px !important",
            }),
            $(" .timelineCloseButton svg", {
                height: "18px !important",
            }),
            $(" .loading", {
                position: "relative",
                top: "calc(50% - 57px)",
                left: "50%",
                textAlign: "center",
                transform: "translate(-50%, -50%)",
            }),
            $(" .entryContainer", {
                padding: "0 !important",
            }),
            $(" .entry", {
                padding: "0 !important",
            }),
            $(" .subtitleContainer", {
                margin: "0 8px 8px 0px !important",
            }),
            $(" .newNotificationsBadge", {
                fontSize: "12px",
                color: theme.palette.canvasColor,
                fontWeight: 500,
                borderRadius: "10px",
                padding: "5px 10px",
                marginLeft: "10px",
            }),
            $(" .markNotifications", {
                marginLeft: "10px",
            }),
            $(" .unseen", {
                backgroundColor: theme.palette.accent1Color,
            }),
            $(" .unread", {
                backgroundColor: theme.palette.accent1Color,
            }),
            $(" .markAllContainer", {
                backgroundColor: theme.palette.canvasColor,
                height: "30px",
                lineHeight: "30px",
                textAlign: "right",
                padding: "0 15px",
                borderBottom: `1px ${theme.palette.lightGrey} solid`,
                fontSize: "12px",
            }),
            $(" .unreadEntry", {
                backgroundColor: theme.palette.unreadBackground,
            }),
            $(" .noNotificationContainer", {
                padding: "12px 24px",
                fontSize: "14px",
                textAlign: "center",
            })
        );
    }, [panelWidth]);

    const entriesRef = React.useRef<INotification[]>(userNotification);
    entriesRef.current = userNotification;
    const polledEntriesRef = React.useRef<INotification[]>();
    const [seenEntries, setSeenEntries] = React.useState<number[]>([]);
    const debouncedSeenEntries = useDebounce(seenEntries.join(","), 1000);

    React.useEffect(() => {
        onFetchUserNotification({ merchant_id: merchantId });
    }, [merchantId]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (debouncedSeenEntries.length) {
            onUpdateUserNotifications({ seen: debouncedSeenEntries.split(","), merchant_id: merchantId });
        }
    }, [debouncedSeenEntries, merchantId]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadNextPage = React.useCallback(() => {
        onFetchUserNotification({ merchant_id: merchantId }, true);
    }, [onFetchUserNotification, merchantId]);

    const handleLoadPolledEntries = React.useCallback(() => {
        onLoadUserNotificationPolledEntries(polledEntriesRef.current);
        entriesRef.current = polledEntriesRef.current;
    }, [onLoadUserNotificationPolledEntries]);

    const handlePolledEntriesSuccess = React.useCallback(({ notifications }: { notifications: INotification[] }) => {
        const currentEntriesTimestamp = entriesRef.current[0] && entriesRef.current[0].created_at;
        const polledEntriesTimestamp = notifications[0] && notifications[0].created_at;
        let differenceDetected = false;
        if (
            currentEntriesTimestamp &&
            polledEntriesTimestamp &&
            new Date(polledEntriesTimestamp).getTime() > new Date(currentEntriesTimestamp).getTime()
        ) {
            polledEntriesRef.current = notifications;
            differenceDetected = true;
        }
        return differenceDetected;
    }, []);

    const rowRenderer = React.useCallback(
        ({ isVisible, index }: { isVisible: boolean; index: number }) => {
            const data = userNotification[index];
            if (isVisible && !seenEntries.includes(index) && !(data.is_seen === true)) {
                setSeenEntries([...seenEntries, index]);
            }
            return (
                <UserActivity
                    activity={data}
                    index={index}
                    merchantId={merchantId}
                    onUpdateUserNotifications={onUpdateUserNotifications}
                />
            );
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [seenEntries, userNotification, merchantId, exportNotifications]
    );

    const handleMarkAllAsRead = React.useCallback(() => {
        onUpdateUserNotifications({ readAll: true, merchant_id: merchantId });
        onUpdateExportNotificationStatus({ readAll: true });
    }, [onUpdateUserNotifications, merchantId, exportNotifications, onUpdateExportNotificationStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    const noRowsRenderer = React.useCallback(() => {
        return (
            !(userNotificationFetching || exportNotifications.exports?.length) && (
                <div className="noNotificationContainer">
                    <p>{formatMessage("You currently have no notifications")}.</p>
                    <p>
                        {formatMessage(
                            "Subscribe to notifications by clicking the Actions menu (3 horizontal dots) for any assortment."
                        )}
                    </p>
                </div>
            )
        );
    }, [userNotificationFetching]); // eslint-disable-line react-hooks/exhaustive-deps

    const pollingURL = React.useMemo(() => {
        return `${siteConfig.goAPI}/v1/user_notification/users/${
            me._id
        }/feeds/${COLLECTION_FEED_SLUG}/items?${stringify({ limit: PAGE_SIZE })}`;
    }, []);

    return (
        <div {...styles}>
            <div data-test={DATATEST.NOTIFICATIONS_CONTAINER} className="userNotificationContainer">
                <div className="userNotificationHeader">
                    <div data-test={DATATEST.NOTIFCATIONS_CONTAINER_TITLE} className="userNotificationHeaderTitle">
                        {formatMessage("Notifications Summary")}
                        {((collectionFeedStats && collectionFeedStats.unseen !== "0") ||
                            exportNotifications?.unseen !== 0) && (
                            <span className="newNotificationsBadge unseen">{`${
                                (exportNotifications ? exportNotifications.unseen : 0) +
                                (collectionFeedStats ? safeParseInt(collectionFeedStats.unseen, 0) : 0)
                            } ${formatMessage("Unseen")}`}</span>
                        )}
                        {((collectionFeedStats && collectionFeedStats.unread !== "0") ||
                            exportNotifications?.unread !== 0) && (
                            <span className="newNotificationsBadge unread">{`${
                                (exportNotifications ? exportNotifications.unread : 0) +
                                (collectionFeedStats ? safeParseInt(collectionFeedStats.unread, 0) : 0)
                            } ${formatMessage("Unread")}`}</span>
                        )}
                        <FlatButton className="timelineCloseButton" onClick={onRequestClose} icon={<Cancel />} />
                    </div>
                    <div className="markAllContainer">
                        {((collectionFeedStats && collectionFeedStats.unread != "0") ||
                            exportNotifications?.unread !== 0) && (
                            <a href="#" className="markNotifications" onClick={handleMarkAllAsRead}>
                                {formatMessage("Mark all as read")}
                            </a>
                        )}
                    </div>
                </div>
                {pollingURL && (
                    <ActivityFeed
                        hasNextPage={userNotificationHasNextPage}
                        isNextPageLoading={userNotificationFetching}
                        entries={userNotification}
                        rowRenderer={rowRenderer}
                        loadNextPage={loadNextPage}
                        onLoadPolledEntries={handleLoadPolledEntries}
                        pollingURL={pollingURL}
                        onPollingSuccess={handlePolledEntriesSuccess}
                        noRowsRenderer={noRowsRenderer}
                        exportNotifications={exportNotifications}
                        onExportWhiteboards={onExportWhiteboards}
                        onUpdateExportNotificationStatus={onUpdateExportNotificationStatus}
                    />
                )}
            </div>
        </div>
    );
};
